import LegalPageLayout from '@/components/layout/LegalPageLayout';

const tocItems = [
  { id: 'intro', label: 'Introduction' },
  { id: 'collection', label: 'Information We Collect' },
  { id: 'usage', label: 'How We Use Information' },
  { id: 'sharing', label: 'Information Sharing' },
  { id: 'security', label: 'Data Security' },
  { id: 'retention', label: 'Data Retention' },
  { id: 'cookies', label: 'Cookies' },
  { id: 'rights', label: 'Your Rights' },
  { id: 'children', label: "Children's Privacy" },
  { id: 'contact', label: 'Contact Us' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="reveal py-10 border-b border-white/[0.04]">
      <h2 className="text-[22px] font-semibold tracking-tight leading-snug mb-5 text-white scroll-mt-[110px]">
        {title}
      </h2>
      <div className="text-sm text-zinc-500 leading-[1.75]">{children}</div>
    </section>
  );
}

function InfoCard({ children, accent }) {
  const border = accent ? 'border-white/[0.12]' : 'border-white/[0.06]';
  const bg = accent ? 'bg-white/[0.04]' : 'bg-white/[0.02]';
  return (
    <div className={`rounded-2xl border ${border} ${bg} p-5 my-5 transition-colors hover:border-white/[0.12]`}>
      {children}
    </div>
  );
}

export default function PrivacyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="ScheduleX is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information."
      tocItems={tocItems}
    >
      <Section id="intro" title="1. Introduction">
        <p className="mb-4">
          ScheduleX (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our scheduling platform.
        </p>
        <p>
          By using ScheduleX, you consent to the practices described in this policy. If you do not agree, please do not use our Service.
        </p>
      </Section>

      <Section id="collection" title="2. Information We Collect">
        <p className="mb-4">We collect information you provide directly to us, including:</p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Account information (name, email address, password)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Profile information (organization name, logo, timezone)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Booking data (meeting titles, descriptions, availability windows)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Guest booking information (names and emails of people who book meetings with you)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Payment information (processed securely by our payment provider)</span>
            </li>
          </ul>
        </InfoCard>
        <p className="mb-4">We also automatically collect certain information when you use our Service:</p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Log data (IP address, browser type, pages visited)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Device information (hardware model, operating system)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Usage data (features used, time spent on pages)</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="usage" title="3. How We Use Your Information">
        <p className="mb-4">We use the information we collect to:</p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Provide, maintain, and improve our Service</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Process transactions and send related information</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Send administrative messages and updates</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Respond to comments and questions</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Monitor and analyze usage patterns to improve user experience</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Detect and prevent fraudulent transactions and other illegal activities</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="sharing" title="4. Information Sharing">
        <p className="mb-4">
          We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
        </p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>With service providers who assist us in operating our Service</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>When required by law or to protect our legal rights</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>In connection with a merger, acquisition, or sale of assets</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>With your consent</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="security" title="5. Data Security">
        <p>
          We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. This includes SSL/TLS encryption for data in transit and encryption for sensitive data at rest.
        </p>
        <InfoCard accent>
          <p className="font-medium text-white mb-1">Encryption</p>
          <p>All data transmitted between your browser and our servers is encrypted using TLS 1.3. Passwords are hashed using bcrypt with a cost factor of 12.</p>
        </InfoCard>
      </Section>

      <Section id="retention" title="6. Data Retention">
        <p>
          We retain your personal information for as long as your account is active or as needed to provide you with our Service. You may request deletion of your account and associated data at any time through the Settings page. Upon deletion, your data is permanently removed from our active systems within 30 days.
        </p>
      </Section>

      <Section id="cookies" title="7. Cookies">
        <p>
          We use cookies and similar tracking technologies to track activity on our Service and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
        </p>
      </Section>

      <Section id="rights" title="8. Your Rights">
        <p className="mb-4">You have the right to:</p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Access the personal information we hold about you</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Correct inaccurate personal information</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Request deletion of your personal information</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Object to or restrict processing of your personal information</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Data portability</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="children" title="9. Children's Privacy">
        <p>
          Our Service is not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal information, we will take steps to delete such information.
        </p>
      </Section>

      <Section id="contact" title="10. Contact Us">
        <p>
          If you have any questions about this Privacy Policy, please contact us at{' '}
          <a href="mailto:privacy@schedulex.site" className="text-white hover:underline">
            privacy@schedulex.site
          </a>.
        </p>
      </Section>
    </LegalPageLayout>
  );
}
